import { useState } from 'react';
import { isNil } from 'es-toolkit';
import { useActiveHouseholdId } from 'src/hooks/use-active-household-id';

import { PERFORMANCE_TIME_PERIOD_ALL_TIME } from '@savvy/constants/performance-time-periods';
import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import type { TransferAdvisorContact } from '../../../utils/funding-phase';
import type { ClientPortalAllocationSlice } from '../../common/allocation';
import type { ClientPortalTimeRange } from '../../common/constants';
import type {
  HomeBenchmark,
  HomeChange,
  HomeDayChange,
  HomeInvestmentRow,
  HomeSeriesPoint,
  HomeTaskRow,
} from '../types';
import { ClientPortalAccountsPortfolioQuery } from '../../../api/accounts-portfolio';
import { ClientPortalBenchmarkComparisonQuery } from '../../../api/benchmark-comparison-query';
import { ClientPortalHomePerformanceQuery } from '../../../api/home-performance-query';
import { ClientPortalHomeTasksQuery } from '../../../api/home-tasks-query';
import { ClientPortalTransferAdvisorQuery } from '../../../api/transfer-advisor-query';
import { useClientPortalFundingPhase } from '../../../hooks/use-client-portal-funding-phase';
import { useOwnOnboardingTaskIds } from '../../../hooks/use-own-onboarding-task-ids';
import { transferAdvisorContact } from '../../../utils/funding-phase';
import { clientPortalAllocationSlicesFromReportHoldings } from '../../common/allocation';
import { CLIENT_PORTAL_DEFAULT_TIME_RANGE } from '../../common/constants';
import { availableTimeRanges, resolveTimeRange, timeRangeAnchor, timeRangeWindowStart } from '../../common/time-ranges';
import { formatMonthAndYear, toFiniteNumber } from '../../common/utils';
import { useClientPortalFirmBranding } from '../../shell/use-client-portal-firm-branding';
import { useClientPortalNav } from '../../shell/use-client-portal-nav';
import {
  balancesFreshness,
  buildInvestmentsSeries,
  buildInvestmentsValueSeries,
  findPeriod,
  formatAsOfDate,
  graphValuesInWindow,
  homeTaskRows,
  investmentRows,
  investmentsTotal,
  latestDayChange,
  seriesAsOfDate,
  twrToPercentPoints,
} from '../utils';

export type ClientPortalHomeState = {
  readonly timeRange: ClientPortalTimeRange;
  readonly timeRanges: readonly ClientPortalTimeRange[];
  readonly setTimeRange: (timeRange: ClientPortalTimeRange) => void;
  readonly valueLoading: boolean;
  readonly valueFailed: boolean;
  readonly retryValue: () => void;
  readonly statsLoading: boolean;
  readonly statsFailed: boolean;
  readonly retryStats: () => void;
  readonly tasksLoading: boolean;
  readonly tasksFailed: boolean;
  readonly retryTasks: () => void;
  readonly allocationLoading: boolean;
  readonly allocationFailed: boolean;
  readonly retryAllocation: () => void;
  readonly allocation: readonly ClientPortalAllocationSlice[];
  readonly investmentsValue: number | null;
  readonly investments: readonly HomeInvestmentRow[];
  readonly brandName: string;
  readonly change: HomeChange;
  readonly series: readonly HomeSeriesPoint[];
  readonly dayChange: HomeDayChange;
  readonly totalReturnAmount: number | null;
  readonly totalReturnPercent: number | null;
  readonly benchmarks: readonly HomeBenchmark[];
  readonly investmentsHref: string | null;
  readonly inceptionLabel: string | null;
  readonly staleAsOfLabel: string | null;
  readonly tasks: readonly HomeTaskRow[];
  readonly transferring: boolean;
  readonly transferTimelineStep: number;
  readonly destinationCustodian: string | null;
  readonly transferAdvisor: TransferAdvisorContact | undefined;
  readonly celebrationKey: string | null;
  readonly choreographyReady: boolean;
};

export function useClientPortalHome(): ClientPortalHomeState {
  const [preferredTimeRange, setPreferredTimeRange] = useState<ClientPortalTimeRange>(CLIENT_PORTAL_DEFAULT_TIME_RANGE);

  const { user } = useAuth();
  const { brandName, loading: brandingLoading } = useClientPortalFirmBranding();
  const { loading: navLoading, personalPortfolioId, investmentsDisabled } = useClientPortalNav();
  const { activeHouseholdId, canQuery } = useActiveHouseholdId();
  const { phase, transferring, transferTimelineStep, destinationCustodian } = useClientPortalFundingPhase();
  const ownOnboardingTaskIds = useOwnOnboardingTaskIds();
  const awaitsFirstOrionSnapshot = phase === 'AWAITING_ACCOUNT_OPENING';
  const { data: transferAdvisorData } = useQueryWithErrorLogging(ClientPortalTransferAdvisorQuery, {
    variables: { id: user?.id ?? '', householdId: activeHouseholdId },
    skip: !transferring || !canQuery || isNil(user?.id),
  });
  const householdId = activeHouseholdId ?? undefined;
  const celebrationIdentity = activeHouseholdId ?? user?.id ?? null;

  const {
    data: performanceData,
    loading: performanceQueryLoading,
    error: performanceError,
    refetch: refetchPerformance,
  } = useQueryWithErrorLogging(ClientPortalHomePerformanceQuery, {
    variables: { portfolioId: personalPortfolioId ?? undefined, householdId },
    skip: navLoading || !canQuery || awaitsFirstOrionSnapshot,
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: tasksData,
    loading: tasksQueryLoading,
    error: tasksError,
    refetch: refetchTasks,
  } = useQueryWithErrorLogging(ClientPortalHomeTasksQuery, {
    variables: { householdId },
    skip: !canQuery,
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: accountsData,
    loading: accountsQueryLoading,
    error: accountsError,
    refetch: refetchAccounts,
  } = useQueryWithErrorLogging(ClientPortalAccountsPortfolioQuery, {
    variables: { id: user?.id ?? '', householdId },
    skip: isNil(user?.id) || !canQuery,
    notifyOnNetworkStatusChange: true,
  });

  const report = performanceData?.portfolioPerformanceReport;
  const periods = report?.performanceOverTime ?? [];
  const allTimePeriod = findPeriod(periods, PERFORMANCE_TIME_PERIOD_ALL_TIME);

  const returnWindow =
    isNil(allTimePeriod) || isNil(allTimePeriod.startDate) || isNil(allTimePeriod.endDate)
      ? null
      : { startDate: allTimePeriod.startDate, endDate: allTimePeriod.endDate };

  const skipBenchmark = !canQuery || isNil(householdId) || isNil(returnWindow);
  const { data: benchmarkData, loading: benchmarkLoading } = useQueryWithErrorLogging(
    ClientPortalBenchmarkComparisonQuery,
    {
      variables: { householdId: householdId ?? '', dateRange: returnWindow ?? { startDate: '', endDate: '' } },
      skip: skipBenchmark,
    },
  );

  const comparison = benchmarkData?.clientPortalBenchmarkComparisons ?? [];

  const itemGroups = accountsData?.portfolio.portfolioItemGroups;
  const inceptionDate = allTimePeriod?.startDate ?? null;
  const wholeHistory = isNil(report)
    ? buildInvestmentsSeries(itemGroups ?? [], PERFORMANCE_TIME_PERIOD_ALL_TIME)
    : buildInvestmentsValueSeries(
        graphValuesInWindow(report.portfolioGraphValues, PERFORMANCE_TIME_PERIOD_ALL_TIME, inceptionDate),
        itemGroups ?? [],
        inceptionDate,
      );
  const timeRanges = availableTimeRanges(wholeHistory.at(0)?.date, seriesAsOfDate(wholeHistory));
  const timeRange = resolveTimeRange(preferredTimeRange, timeRanges);
  const rangePeriod = findPeriod(periods, timeRange);
  const windowStart =
    rangePeriod?.startDate ??
    timeRangeWindowStart(timeRange, timeRangeAnchor(seriesAsOfDate(wholeHistory)))?.toISODate() ??
    null;
  const managedValues = graphValuesInWindow(report?.portfolioGraphValues, timeRange, windowStart);
  const balanceSeries = buildInvestmentsSeries(itemGroups ?? [], timeRange);
  const series = isNil(report)
    ? balanceSeries
    : buildInvestmentsValueSeries(managedValues, itemGroups ?? [], windowStart);
  const investments = investmentRows(itemGroups);

  const freshness = balancesFreshness(balanceSeries);

  const valueLoading = !canQuery || isNil(user?.id) || accountsQueryLoading || brandingLoading;
  const statsLoading = navLoading || !canQuery || performanceQueryLoading;
  const statsFailed = !isNil(performanceError) && !performanceQueryLoading;
  const linkInvestments = !investmentsDisabled && !isNil(personalPortfolioId);

  const transferAdvisorHousehold = transferAdvisorData?.client.household;

  return {
    transferring,
    transferTimelineStep,
    destinationCustodian,
    transferAdvisor: transferAdvisorContact(
      transferAdvisorHousehold?.advisoryTeam ?? [],
      transferAdvisorHousehold?.advisor?.id,
    ),
    timeRange,
    timeRanges,
    setTimeRange: setPreferredTimeRange,
    valueLoading,
    valueFailed: !isNil(accountsError) && !accountsQueryLoading,
    retryValue: () => {
      void refetchAccounts();
    },
    statsLoading,
    statsFailed,
    retryStats: () => {
      void refetchPerformance();
    },
    tasksLoading: !canQuery || tasksQueryLoading,
    tasksFailed: !isNil(tasksError) && !tasksQueryLoading,
    retryTasks: () => {
      void refetchTasks();
    },
    allocationLoading: statsLoading,
    allocationFailed: statsFailed,
    retryAllocation: () => {
      void refetchPerformance();
    },
    allocation: clientPortalAllocationSlicesFromReportHoldings(
      report?.holdings,
      performanceData?.clientPortalAllocationSortByRatio ?? false,
    ),
    investmentsValue: investmentsTotal(investments),
    investments,
    brandName,
    change: { amount: toFiniteNumber(rangePeriod?.usdReturn), percent: twrToPercentPoints(rangePeriod?.twr) },
    series,
    dayChange: latestDayChange(balanceSeries),
    totalReturnAmount: toFiniteNumber(allTimePeriod?.usdReturn),
    totalReturnPercent: twrToPercentPoints(allTimePeriod?.twr),
    benchmarks: comparison.map((entry) => ({
      label: entry.label,
      percent: twrToPercentPoints(entry.returnCumulative),
    })),
    investmentsHref: linkInvestments ? `/savvy-portfolio/${personalPortfolioId}` : null,
    inceptionLabel: formatMonthAndYear(allTimePeriod?.startDate),
    staleAsOfLabel: freshness.kind === 'stale' ? formatAsOfDate(freshness.asOfDate) : null,
    tasks: homeTaskRows(tasksData?.tasks, ownOnboardingTaskIds),
    // Null rather than a shared fallback while identity is unresolved: two clients on one browser profile
    // would otherwise file their celebrations under the same ledger entry and suppress each other's.
    celebrationKey: celebrationIdentity === null ? null : `client-portal-home:${celebrationIdentity}`,
    // The benchmark waits on performance and so lands last; leave it out and its tile pops in after the row
    // has settled, reflowing the tiles from two columns to three.
    choreographyReady: !valueLoading && !statsLoading && !(!skipBenchmark && benchmarkLoading),
  };
}
